import { useState } from 'react';
import api from '../../hooks/useApi';
import FilePreviewModal from '../shared/FilePreviewModal';

// The invoice document and the days it bills, checked against the PLOW.
//
// A contractor bills a line per day, at their normal rate or a higher one for
// an evening or a working-away day. The server reads those lines off the
// document and checks each against the contractor's PLOW cells (the rules are
// in src/lib/invoicePlowMatch.js); this panel shows the result, lets the
// reviewer correct a misread line, and keeps the document on the invoice.

function fmtMoney(v) {
  if (v == null) return '—';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const fmtDay = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STATUS = {
  ok:    { label: 'Correct', icon: '✓', badge: 'bg-green-50 text-green-700 border-green-300', text: 'text-green-700' },
  warn:  { label: 'Warning', icon: '⚠', badge: 'bg-amber-50 text-amber-700 border-amber-300', text: 'text-amber-700' },
  error: { label: 'Error',   icon: '✕', badge: 'bg-red-50 text-red-700 border-red-300',       text: 'text-red-700' },
};

const BAND = {
  normal:    { label: 'Normal',    cls: 'bg-gray-100 text-gray-600' },
  increased: { label: 'Increased', cls: 'bg-purple-100 text-purple-700' },
  half:      { label: 'Half day',  cls: 'bg-blue-100 text-blue-700' },
  other:     { label: 'Other',     cls: 'bg-amber-100 text-amber-700' },
  unknown:   { label: '?',         cls: 'bg-gray-100 text-gray-500' },
};

const RATE_SOURCE = {
  invoice: 'set on this invoice',
  contract: "the contract's day rate",
  lowest: 'the lowest day on the invoice',
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.ok;
  return <span className={`text-[8px] px-1.5 py-0.5 rounded border font-semibold whitespace-nowrap ${s.badge}`}>{s.icon} {s.label}</span>;
}

// Invoice days against PLOW days, for one band, and how many agree day by day.
function CountTile({ label, invoice, plow, agreed }) {
  const allAgree = Math.abs(invoice - plow) < 0.01 && Math.abs(invoice - agreed) < 0.01;
  return (
    <div className="bg-gray-50 rounded px-3 py-2 flex-1" style={{ minWidth: 170 }}>
      <div className="text-[8px] font-semibold text-gray-500 uppercase">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5 flex-wrap">
        <span className="text-[13px] font-bold text-navy">{fmtDays(invoice)}</span>
        <span className="text-[9px] text-gray-500 mr-1.5">invoiced</span>
        <span className="text-[13px] font-bold text-navy">{fmtDays(plow)}</span>
        <span className="text-[9px] text-gray-500">on the PLOW</span>
      </div>
      <div className={`text-[8px] font-semibold mt-0.5 ${allAgree ? 'text-green-700' : 'text-amber-700'}`}>
        {allAgree ? '✓ Every day agrees'
          : Math.abs(agreed - invoice) < 0.01 ? `⚠ Every invoiced day agrees; the PLOW has ${fmtDays(plow - invoice)} more`
          : `⚠ ${fmtDays(agreed)} of the ${fmtDays(invoice)} invoiced agree with the PLOW`}
      </div>
    </div>
  );
}

const blankLine = () => ({ work_date: '', description: '', line_type: 'labour', amount: '', source: 'manual' });

export default function InvoicePlowCheck({ detail, onChanged }) {
  const match = detail.plow_match || {};
  const editable = (detail.lines || []).filter(l => l.source === 'document' || l.source === 'manual');

  // Attach and read: one message under the buttons, where the click was.
  const [busy, setBusy] = useState(null); // 'attach' | 'read' | null
  const [message, setMessage] = useState(null); // { tone: 'error' | 'ok', text }
  // The document opens in a preview with a Download button, not as a download.
  const [viewing, setViewing] = useState(false);

  const readDays = async ({ confirmReplace = true } = {}) => {
    if (confirmReplace && editable.length && !window.confirm('Reading the invoice again replaces the lines below, including any you corrected. Read it again?')) return;
    setBusy('read'); setMessage(null);
    try {
      const res = await api.post(`resource-invoices/${detail.id}/read-days`, {});
      setMessage({ tone: 'ok', text: `Read ${res.lines} line${res.lines === 1 ? '' : 's'} from the invoice.` });
      onChanged();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'The invoice could not be read.' });
    } finally {
      setBusy(null);
    }
  };

  const attach = async (file) => {
    if (!file) return;
    setBusy('attach'); setMessage(null);
    try {
      const data = await fileToBase64(file);
      await api.post(`resource-invoices/${detail.id}/document`, { filename: file.name, content_type: file.type, data });
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'The document could not be attached.' });
      setBusy(null);
      return;
    }
    // Stored, so the link shows whether or not the read below succeeds.
    // Reading it straight away is the reason it was attached.
    onChanged();
    await readDays({ confirmReplace: false });
  };

  // Line editor.
  const [edit, setEdit] = useState(null); // { lines, error, saving } while editing
  const startEdit = () => setEdit({
    lines: editable.length
      ? editable.map(l => ({ work_date: l.work_date || '', description: l.description || '', line_type: l.line_type === 'expense' ? 'expense' : 'labour', amount: String(l.amount ?? ''), source: l.source }))
      : [blankLine()],
    error: '', saving: false,
  });
  const setLine = (i, patch) => setEdit(e => ({ ...e, error: '', lines: e.lines.map((l, j) => (j === i ? { ...l, ...patch, source: 'manual' } : l)) }));
  const saveLines = async () => {
    const lines = edit.lines.filter(l => l.description.trim() || l.amount !== '' || l.work_date);
    if (lines.some(l => l.amount === '' || isNaN(Number(l.amount)))) { setEdit(e => ({ ...e, error: 'Give every line an amount.' })); return; }
    setEdit(e => ({ ...e, saving: true, error: '' }));
    try {
      await api.put(`resource-invoices/${detail.id}/day-lines`, {
        lines: lines.map(l => ({ ...l, work_date: l.work_date || null, amount: Number(l.amount) })),
      });
      setEdit(null);
      onChanged();
    } catch (e) {
      setEdit(x => ({ ...x, saving: false, error: e.message || 'The lines could not be saved.' }));
    }
  };
  const editTotal = (edit?.lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0);

  // The normal day rate the bands are measured from.
  const [rateEdit, setRateEdit] = useState(null); // { value, error, saving }
  const saveRate = async () => {
    setRateEdit(r => ({ ...r, saving: true, error: '' }));
    try {
      await api.put(`resource-invoices/${detail.id}`, { day_rate: rateEdit.value === '' ? null : Number(rateEdit.value) });
      setRateEdit(null);
      onChanged();
    } catch (e) {
      setRateEdit(r => ({ ...r, saving: false, error: e.message || 'The rate could not be saved.' }));
    }
  };

  const summary = match.summary;
  const rows = match.rows || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-[9px] font-bold text-gray-400 uppercase">Days against the PLOW</div>
        {summary && <StatusBadge status={summary.status} />}
      </div>

      {/* The document: kept on the invoice, and where the days are read from. */}
      <div className="flex items-center gap-2 flex-wrap text-[9px] mb-2">
        {detail.file_url
          ? <button type="button" onClick={() => setViewing(true)} className="text-blue-600 hover:underline">📎 View invoice document</button>
          : <span className="text-amber-700">No invoice document is stored on this invoice.</span>}
        <label className={`px-2 py-0.5 rounded border border-gray-300 bg-white text-gray-600 font-semibold cursor-pointer hover:bg-gray-50 ${busy ? 'opacity-40 pointer-events-none' : ''}`}>
          {busy === 'attach' ? 'Attaching…' : detail.file_url ? 'Replace document' : 'Attach invoice (PDF or photo)'}
          <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={e => { attach(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {detail.file_url && (
          <button onClick={() => readDays()} disabled={!!busy}
            className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
            {busy === 'read' ? 'Reading the invoice…' : editable.length ? 'Read the days again' : 'Read the days from the invoice'}
          </button>
        )}
        {!edit && (
          <button onClick={startEdit} disabled={!!busy} className="text-blue-600 hover:underline disabled:opacity-40">
            {editable.length ? 'Correct the lines' : 'Enter the days by hand'}
          </button>
        )}
      </div>
      {message && <div className={`text-[9px] mb-2 ${message.tone === 'error' ? 'text-red-600' : 'text-green-700'}`}>{message.text}</div>}
      {viewing && detail.file_url && (
        <FilePreviewModal url={detail.file_url}
          title={`${detail.invoice_number ? `Invoice ${detail.invoice_number}` : 'Invoice'}${detail.worker_name ? `: ${detail.worker_name}` : ''}`}
          onClose={() => setViewing(false)} />
      )}

      {edit && (
        <div className="bg-gray-50 rounded p-2 mb-3">
          <div className="text-[8px] font-semibold text-gray-500 uppercase mb-1">Invoice lines</div>
          <div className="space-y-1">
            {edit.lines.map((l, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input type="date" className="form-input text-[9px] py-0.5" style={{ width: 120 }} value={l.work_date}
                  onChange={e => setLine(i, { work_date: e.target.value })} />
                <input className="form-input text-[9px] py-0.5 flex-1" placeholder="Description" value={l.description}
                  onChange={e => setLine(i, { description: e.target.value })} />
                <select className="form-input text-[9px] py-0.5" style={{ width: 84 }} value={l.line_type}
                  onChange={e => setLine(i, { line_type: e.target.value })}>
                  <option value="labour">Day</option>
                  <option value="expense">Expense</option>
                </select>
                <input type="number" min="0" step="0.01" className="form-input text-[9px] py-0.5" style={{ width: 80 }} placeholder="£" value={l.amount}
                  onChange={e => setLine(i, { amount: e.target.value })} />
                <button onClick={() => setEdit(e => ({ ...e, lines: e.lines.filter((_, j) => j !== i) }))}
                  className="text-red-400 hover:text-red-600 text-[9px] px-1" title="Remove this line">✕</button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2 text-[9px] flex-wrap">
            <button onClick={() => setEdit(e => ({ ...e, lines: [...e.lines, blankLine()] }))} className="text-blue-600 hover:underline">+ Add a line</button>
            <span className="text-gray-500 ml-auto">
              Lines total {fmtMoney(editTotal)}
              {detail.total_amount != null && Math.abs(editTotal - Number(detail.total_amount)) > 0.005 && (
                <span className="text-amber-700"> · invoice total {fmtMoney(detail.total_amount)}</span>
              )}
            </span>
            <button onClick={saveLines} disabled={edit.saving} className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
              {edit.saving ? 'Saving…' : 'Save lines'}</button>
            <button onClick={() => setEdit(null)} className="text-gray-500 hover:underline">Keep the lines as they were</button>
          </div>
          {edit.error && <div className="text-[9px] text-red-600 mt-1">{edit.error}</div>}
        </div>
      )}

      {match.unavailable === 'no_person' && (
        <div className="text-[9px] text-amber-700">
          {detail.worker_name} is not linked to a staff record, so their PLOW cannot be read. Link them under People, External Resource Record.
        </div>
      )}
      {match.unavailable === 'no_days' && !edit && (
        <div className="text-[9px] text-gray-500">
          No days on this invoice yet. {detail.file_url ? 'Read them from the invoice document, or enter them by hand.' : 'Attach the invoice document to read them, or enter them by hand.'}
        </div>
      )}
      {match.unavailable === 'error' && <div className="text-[9px] text-red-600">The PLOW could not be checked just now. Reload to try again.</div>}

      {summary && (
        <>
          <div className="flex gap-2 flex-wrap mb-2">
            <CountTile label="Normal rate days" invoice={summary.invoice.normal} plow={summary.plow.normal} agreed={summary.agreed.normal} />
            <CountTile label="Increased rate days" invoice={summary.invoice.increased} plow={summary.plow.increased} agreed={summary.agreed.increased} />
            <div className="bg-gray-50 rounded px-3 py-2 flex-1" style={{ minWidth: 150 }}>
              <div className="text-[8px] font-semibold text-gray-500 uppercase">Days checked</div>
              <div className="flex gap-2 mt-1 text-[10px] font-bold">
                <span className={STATUS.ok.text}>✓ {summary.counts.ok}</span>
                <span className={STATUS.warn.text}>⚠ {summary.counts.warn}</span>
                <span className={STATUS.error.text}>✕ {summary.counts.error}</span>
              </div>
              <div className="text-[8px] text-gray-500 mt-0.5">PLOW: {match.person_name}</div>
            </div>
          </div>

          <div className="text-[9px] text-gray-500 mb-2">
            {!rateEdit ? (
              <>
                Normal rate {match.normal_rate ? <span className="font-semibold text-navy">{fmtMoney(match.normal_rate)}</span> : 'not known'}
                {match.normal_rate_source && `, ${RATE_SOURCE[match.normal_rate_source]}`}. A day billed above it is an increased day,
                which the PLOW supports with a shift outside 7am to 6pm or a working-away job.
                <button onClick={() => setRateEdit({ value: detail.day_rate ?? '', error: '', saving: false })} className="ml-1 text-blue-600 hover:underline">Change rate</button>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 flex-wrap">
                Normal rate £
                <input type="number" min="0" step="0.01" className="form-input text-[9px] py-0.5" style={{ width: 80 }} value={rateEdit.value}
                  placeholder={match.normal_rate ? String(match.normal_rate) : ''}
                  onChange={e => setRateEdit(r => ({ ...r, value: e.target.value, error: '' }))} />
                <button onClick={saveRate} disabled={rateEdit.saving} className="px-2 py-0.5 rounded bg-navy text-white font-semibold disabled:opacity-40">
                  {rateEdit.saving ? 'Saving…' : 'Save rate'}</button>
                <button onClick={() => setRateEdit(null)} className="text-gray-500 hover:underline">Keep {match.normal_rate ? fmtMoney(match.normal_rate) : 'as it is'}</button>
                <span className="text-gray-400">Leave blank to use the contract's rate or the lowest day.</span>
                {rateEdit.error && <span className="text-red-600">{rateEdit.error}</span>}
              </span>
            )}
          </div>

          {summary.issues.map((i, k) => (
            <div key={k} className={`text-[9px] mb-1 ${STATUS[i.level].text}`}>{STATUS[i.level].icon} {i.text}</div>
          ))}

          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-[9px]">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-left">
                  <th className="px-2 py-1 font-semibold" style={{ width: 80 }}>Day</th>
                  <th className="px-2 py-1 font-semibold">Invoice</th>
                  <th className="px-2 py-1 font-semibold">PLOW</th>
                  <th className="px-2 py-1 font-semibold" style={{ width: '32%' }}>Check</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.date} className="border-t border-gray-100 align-top">
                    <td className="px-2 py-1.5 font-semibold text-navy whitespace-nowrap">{fmtDay(r.date)}</td>
                    <td className="px-2 py-1.5">
                      {r.billed ? (
                        <>
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-navy">{fmtMoney(r.amount)}</span>
                            <span className={`text-[7px] px-1 py-0.5 rounded font-semibold ${BAND[r.band]?.cls || BAND.unknown.cls}`}>{BAND[r.band]?.label || r.band}</span>
                          </div>
                          <div className="text-gray-500">{r.description}</div>
                        </>
                      ) : <span className="text-gray-400">Not billed</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.plow.jobs.length ? r.plow.jobs.map((j, k) => (
                        <div key={k} className="text-gray-600">
                          {j.name}{j.sow_reference && <span className="text-gray-400"> · {j.sow_reference}</span>}
                          {j.section && !['day', ''].includes(String(j.section).toLowerCase()) && <span className="text-gray-400"> ({j.section})</span>}
                        </div>
                      )) : <div className="text-gray-400">{r.plow.status ? r.plow.status.label : 'Nothing booked'}</div>}
                      {r.plow.days > 0 && (
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          {r.plow.shift && <span className="text-gray-500">{r.plow.shift}</span>}
                          {r.plow.increased
                            ? <span className="text-[7px] px-1 py-0.5 rounded font-semibold bg-purple-100 text-purple-700" title={r.plow.reasons.join('; ')}>Increased</span>
                            : <span className="text-[7px] px-1 py-0.5 rounded font-semibold bg-gray-100 text-gray-600">Usual hours</span>}
                          {r.plow.days === 0.5 && <span className="text-[7px] px-1 py-0.5 rounded font-semibold bg-blue-100 text-blue-700">Half day</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <StatusBadge status={r.status} />
                      {r.issues.map((i, k) => <div key={k} className={`mt-0.5 ${STATUS[i.level].text}`}>{i.text}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
